import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api.js";
import { IconArrow, IconCheck, IconX } from "../components/icons.jsx";

function fmt(cents, cur) {
  if (cents == null) return "—";
  return (cents / 100).toFixed(2) + " " + (cur || "EUR");
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toISOString().slice(0, 16).replace("T", " ");
}

export default function AdminPlansPage() {
  const { t } = useT();
  const toast = useToast();
  const [plans, setPlans] = useState(null);
  const [packs, setPacks] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editingPack, setEditingPack] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingPack, setCreatingPack] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [message, setMessage] = useState(null);

  async function load() {
    const r = await api("/admin/plans");
    setPlans(r.plans);
    const p = await api("/admin/slot-packs");
    setPacks(p.packs);
  }

  useEffect(() => {
    load().catch((err) => toast.error(err.message));
  }, []);

  async function sync(provider) {
    setSyncing(provider);
    setMessage(null);
    try {
      const res = await api(`/admin/sync/${provider}`, { method: "POST" });
      if (res.errors && res.errors.length) {
        toast.warn(
          `${provider} sync finished with ${res.errors.length} error(s). Plans synced: ${res.plans_synced}, packs: ${res.packs_synced}. First error: ${res.errors[0].error}`
        );
      } else {
        toast.success(
          `${provider} sync done — ${res.plans_synced} plans, ${res.packs_synced} packs.`
        );
      }
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSyncing(null);
    }
  }

  async function saveEdit(planId, patch) {
    try {
      await api(`/admin/plans/${planId}`, { method: "PUT", body: patch });
      setEditing(null);
      await load();
      toast.success("Plan updated.");
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function deletePlan(id) {
    if (!confirm("Delete this plan? Subscriptions referencing it will lose the link.")) {
      return;
    }
    try {
      await api(`/admin/plans/${id}`, { method: "DELETE" });
      await load();
      toast.success("Plan deleted.");
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function createPlan(data) {
    try {
      await api("/admin/plans", { method: "POST", body: data });
      setCreating(false);
      await load();
      toast.success("Plan created. Don't forget to sync to your providers.");
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function savePack(id, data) {
    try {
      if (id) {
        // No PUT endpoint for packs in admin.js, only POST/DELETE.
        // We delete + recreate, but only if there's no fk dependency.
        await api(`/admin/slot-packs/${id}`, { method: "DELETE" });
      }
      await api("/admin/slot-packs", { method: "POST", body: data });
      setEditingPack(null);
      setCreatingPack(false);
      await load();
      toast.success("Slot pack saved.");
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function deletePack(id) {
    if (!confirm("Delete this slot pack?")) return;
    try {
      await api(`/admin/slot-packs/${id}`, { method: "DELETE" });
      await load();
      toast.success("Slot pack deleted.");
    } catch (err) {
      toast.error(err.message);
    }
  }

  if (!plans || !packs) return <div className="loading">Loading plans</div>;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Administration</div>
          <h1 className="h1">Plans &amp; packs</h1>
        </div>
        <div className="ph-meta">
          {plans.length} plans · {packs.length} packs
        </div>
      </div>

      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{ marginBottom: 18 }}
          >
            <Banner message={message} onClose={() => setMessage(null)} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sync section */}
      <div className="section">
        <div className="section-head">
          <h2>Provider sync</h2>
        </div>
        <div className="section-body">
          <p className="dim" style={{ fontSize: 13, marginTop: 0 }}>
            Push your plans and packs to Stripe/PayPal. Creates products and prices,
            updates IDs on each item. Re-run after editing prices.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button
              className="btn primary"
              disabled={syncing === "stripe"}
              onClick={() => sync("stripe")}
            >
              {syncing === "stripe" ? "Syncing…" : "Sync to Stripe"} <IconArrow size={12} />
            </button>
            <button
              className="btn primary"
              disabled={syncing === "paypal"}
              onClick={() => sync("paypal")}
            >
              {syncing === "paypal" ? "Syncing…" : "Sync to PayPal"} <IconArrow size={12} />
            </button>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div className="section">
        <div className="section-head">
          <h2>Subscription plans</h2>
          <button className="btn ghost" onClick={() => setCreating(true)}>
            + New plan
          </button>
        </div>
        <div className="section-body" style={{ padding: 0 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Slots</th>
                <th>Monthly</th>
                <th>Yearly</th>
                <th>Stripe</th>
                <th>PayPal</th>
                <th>Synced</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td><span className="mono">{p.code}</span></td>
                  <td>{p.name}</td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {p.device_slots}d · {p.viewer_slots}v
                  </td>
                  <td className="mono">{fmt(p.price_monthly_cents, p.currency)}</td>
                  <td className="mono">{fmt(p.price_yearly_cents, p.currency)}</td>
                  <td>
                    <ProviderIndicator
                      ok={!!(p.stripe_price_monthly_id || p.stripe_price_yearly_id)}
                      label="stripe"
                    />
                  </td>
                  <td>
                    <ProviderIndicator
                      ok={!!(p.paypal_plan_monthly_id || p.paypal_plan_yearly_id)}
                      label="paypal"
                    />
                  </td>
                  <td className="mono" style={{ fontSize: 10.5, color: "var(--text-dim)" }}>
                    {p.last_synced_at ? fmtDate(p.last_synced_at) : "never"}
                  </td>
                  <td>
                    {p.is_active ? <span className="chip ok">active</span> : <span className="chip">inactive</span>}
                    {p.is_free && <span className="chip acc" style={{ marginLeft: 4 }}>free</span>}
                  </td>
                  <td style={{ display: "flex", gap: 4 }}>
                    <button className="btn ghost" onClick={() => setEditing(p)}>Edit</button>
                    {!p.is_free && (
                      <button className="btn ghost danger" onClick={() => deletePlan(p.id)}>
                        <IconX size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slot packs */}
      <div className="section">
        <div className="section-head">
          <h2>Slot packs (one-time)</h2>
          <button className="btn ghost" onClick={() => setCreatingPack(true)}>
            + New pack
          </button>
        </div>
        <div className="section-body" style={{ padding: 0 }}>
          {packs.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--text-dim)" }}>
              <span className="serif" style={{ fontStyle: "italic", fontSize: 18 }}>
                No slot packs configured.
              </span>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Slots</th>
                  <th>Price</th>
                  <th>Stripe</th>
                  <th>PayPal</th>
                  <th>Synced</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {packs.map((p) => (
                  <tr key={p.id}>
                    <td><span className="mono">{p.code}</span></td>
                    <td>{p.name}</td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      +{p.device_slots}d · +{p.viewer_slots}v
                    </td>
                    <td className="mono">{fmt(p.price_cents, p.currency)}</td>
                    <td><ProviderIndicator ok={!!p.stripe_price_id} label="stripe" /></td>
                    <td><ProviderIndicator ok={!!p.paypal_product_id} label="paypal" /></td>
                    <td className="mono" style={{ fontSize: 10.5, color: "var(--text-dim)" }}>
                      {p.last_synced_at ? fmtDate(p.last_synced_at) : "never"}
                    </td>
                    <td>
                      {p.is_active ? <span className="chip ok">active</span> : <span className="chip">inactive</span>}
                    </td>
                    <td>
                      <button className="btn ghost danger" onClick={() => deletePack(p.id)}>
                        <IconX size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && (
        <PlanEditModal plan={editing} onClose={() => setEditing(null)} onSave={saveEdit} />
      )}
      {creating && (
        <PlanCreateModal onClose={() => setCreating(false)} onSave={createPlan} />
      )}
      {creatingPack && (
        <PackCreateModal onClose={() => setCreatingPack(false)} onSave={(d) => savePack(null, d)} />
      )}
    </>
  );
}

function ProviderIndicator({ ok, label }) {
  if (ok) {
    return (
      <span className="chip ok" style={{ fontSize: 9.5 }}>
        <IconCheck size={10} /> synced
      </span>
    );
  }
  return <span className="chip" style={{ fontSize: 9.5, color: "var(--text-faint)" }}>—</span>;
}

function PlanEditModal({ plan, onClose, onSave }) {
  const [form, setForm] = useState({
    name: plan.name,
    description: plan.description || "",
    device_slots: plan.device_slots,
    viewer_slots: plan.viewer_slots,
    price_monthly_cents: plan.price_monthly_cents,
    price_yearly_cents: plan.price_yearly_cents,
    is_active: !!plan.is_active,
    billing_mode: plan.billing_mode,
    currency: plan.currency || "EUR",
  });

  return (
    <ModalShell title="Edit plan" subtitle={plan.code} onClose={onClose}>
      <PlanFormFields form={form} setForm={setForm} />
      <ModalActions onCancel={onClose} onSave={() => onSave(plan.id, form)} />
    </ModalShell>
  );
}

function PlanCreateModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    device_slots: 5,
    viewer_slots: 3,
    is_free: false,
    is_active: true,
    price_monthly_cents: 999,
    price_yearly_cents: 9990,
    billing_mode: "subscription",
    currency: "EUR",
  });

  return (
    <ModalShell title="New plan" onClose={onClose}>
      <div className="field">
        <label>Code</label>
        <input
          type="text"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        />
        <span className="hint">Lowercase identifier (e.g. "starter")</span>
      </div>
      <PlanFormFields form={form} setForm={setForm} />
      <ModalActions onCancel={onClose} onSave={() => onSave(form)} />
    </ModalShell>
  );
}

function PlanFormFields({ form, setForm }) {
  return (
    <>
      <div className="field">
        <label>Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Description</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="field">
          <label>Device slots</label>
          <input
            type="number"
            min={0}
            value={form.device_slots}
            onChange={(e) => setForm({ ...form, device_slots: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label>Viewer slots</label>
          <input
            type="number"
            min={0}
            value={form.viewer_slots}
            onChange={(e) => setForm({ ...form, viewer_slots: Number(e.target.value) })}
          />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="field">
          <label>Monthly (cents)</label>
          <input
            type="number"
            min={0}
            value={form.price_monthly_cents ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                price_monthly_cents: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
        </div>
        <div className="field">
          <label>Yearly (cents)</label>
          <input
            type="number"
            min={0}
            value={form.price_yearly_cents ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                price_yearly_cents: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
        </div>
      </div>
      <div className="field">
        <label>Billing mode</label>
        <select
          value={form.billing_mode}
          onChange={(e) => setForm({ ...form, billing_mode: e.target.value })}
        >
          <option value="none">none</option>
          <option value="subscription">subscription</option>
          <option value="onetime">onetime</option>
          <option value="both">both</option>
        </select>
      </div>
      <div className="field">
        <label>Currency</label>
        <input
          type="text"
          maxLength={3}
          value={form.currency}
          onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
        />
      </div>
    </>
  );
}

function PackCreateModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    device_slots: 5,
    viewer_slots: 0,
    price_cents: 999,
    currency: "EUR",
    is_active: true,
  });

  return (
    <ModalShell title="New slot pack" onClose={onClose}>
      <div className="field">
        <label>Code</label>
        <input
          type="text"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Description</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="field">
          <label>+Device slots</label>
          <input
            type="number"
            min={0}
            value={form.device_slots}
            onChange={(e) => setForm({ ...form, device_slots: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label>+Viewer slots</label>
          <input
            type="number"
            min={0}
            value={form.viewer_slots}
            onChange={(e) => setForm({ ...form, viewer_slots: Number(e.target.value) })}
          />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="field">
          <label>Price (cents)</label>
          <input
            type="number"
            min={0}
            value={form.price_cents}
            onChange={(e) => setForm({ ...form, price_cents: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label>Currency</label>
          <input
            type="text"
            maxLength={3}
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
          />
        </div>
      </div>
      <ModalActions onCancel={onClose} onSave={() => onSave(form)} />
    </ModalShell>
  );
}

function ModalShell({ title, subtitle, children, onClose }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100,
        padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--line-strong)",
          borderRadius: "var(--r-lg)",
          padding: 28,
          width: "100%",
          maxWidth: 520,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px var(--acc-glow-2)",
        }}
      >
        <div className="eyebrow">{title}</div>
        {subtitle && (
          <div className="mono dim" style={{ fontSize: 11, marginTop: 4, marginBottom: 18 }}>
            {subtitle}
          </div>
        )}
        {!subtitle && <div style={{ marginBottom: 18 }} />}
        {children}
      </motion.div>
    </div>
  );
}

function ModalActions({ onCancel, onSave }) {
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 24 }}>
      <button className="btn ghost" onClick={onCancel} style={{ flex: 1 }}>
        <IconX size={12} /> Cancel
      </button>
      <button className="btn primary" style={{ flex: 1 }} onClick={onSave}>
        <IconCheck size={12} /> Save
      </button>
    </div>
  );
}

function Banner({ message, onClose }) {
  const kind = message.ok ? "ok" : message.warn ? "warn" : "err";
  const text = message.ok || message.warn || message.error;
  const bg =
    kind === "ok" ? "rgba(74, 222, 128, 0.08)" :
    kind === "warn" ? "rgba(251, 191, 36, 0.08)" :
    "rgba(248, 113, 113, 0.08)";
  const border =
    kind === "ok" ? "rgba(74, 222, 128, 0.3)" :
    kind === "warn" ? "rgba(251, 191, 36, 0.3)" :
    "rgba(248, 113, 113, 0.3)";
  const color =
    kind === "ok" ? "var(--ok)" :
    kind === "warn" ? "var(--warn)" :
    "var(--err)";
  return (
    <div
      style={{
        padding: "12px 16px",
        background: bg,
        border: `1px solid ${border}`,
        borderLeft: `2px solid ${color}`,
        borderRadius: "var(--r)",
        color,
        fontFamily: "var(--mono)",
        fontSize: 12,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}
    >
      <span>{text}</span>
      <button onClick={onClose} style={{ background: "transparent", border: "none", color, cursor: "pointer", display: "flex" }}>
        <IconX size={14} />
      </button>
    </div>
  );
}
